import React, { useState } from 'react';
import {
  Modal,
  ModalFooter,
  ModalContent,
  ModalOverlay,
  FormControl,
  Box,
} from '@chakra-ui/react';
import { Button, Text, Colors, Checkbox } from '@mercator-gcp/grout';
import { capitalize } from 'lodash';
import { PlusButton } from 'components/elements/Button';
import { Loading } from 'components/elements/Loading';
import { Text as CKText } from 'components/elements/Text';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { AddCollectionName } from 'components/modals/AddCollectionName';
import { useCollections } from 'hooks/useCollections';
import { useSavedProjects } from 'hooks/useSavedProjects';
import { CategoryEnum } from 'types/SavedProject';

const Separator = () => (
  <Box
    sx={{
      height: '1px',
      backgroundColor: Colors.ArmyGreen200,
      width: '100%',
    }}
  />
);

const modalHeaderStyle = {
  padding: 'padding_16',
  paddingBottom: '0px',
};

const modalBodyStyle = {
  padding: 'padding_16',
  paddingBottom: '0px',
};
const formHeaderStyle = { paddingBottom: 'padding_16' };

const formBodyStyle = {
  width: '100%',
  maxHeight: '400px',
  overflowY: 'auto',
};

const checkboxStyle = {
  paddingY: 'padding_16',
  borderRadius: '4px',
};

const checkboxLabelStyle = {
  marginLeft: 'padding_16',
  justifyContent: 'normal',
};

const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: 'padding_16',
};

type Props = {
  onClose(): void;
  isOpen: boolean;
  projectCategory: string;
  currentProjectCategory?: string;
  projectSlug?: string;
  documentId?: string;
  isUnhidingProject?: boolean;
};

const AddProjectToCollectionModal = ({
  onClose,
  isOpen,
  projectCategory,
  currentProjectCategory = 'uncategorized',
  projectSlug,
  documentId,
  isUnhidingProject,
}: Props) => {
  const [selectedItems, setSelectedItems] = useState<number | undefined>();
  const [createNew, setCreateNew] = useState<boolean>(false);
  const { getCollectionsList } = useCollections();
  const { data: collectionList } = getCollectionsList;
  const { watchProject, starProject } = useSavedProjects();
  const handleSaveProject = (collectionId: number) => {
    if (projectCategory === CategoryEnum.WATCH) {
      watchProject.mutate({
        collectionId,
        projectSlug,
        documentId,
        isUnhidingProject,
        currentProjectCategory,
      });
    } else if (projectCategory === CategoryEnum.STAR) {
      starProject.mutate({
        collectionId,
        projectSlug,
        documentId,
        isUnhidingProject,
        currentProjectCategory,
      });
    }
  };

  const handleConfirm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedItems) {
      handleSaveProject(selectedItems);
      setSelectedItems(undefined);
    }
    onClose();
  };

  const onNewCollectionClose = () => {
    setCreateNew(false);
  };

  const onNewCollectionSuccess = (collectionId: number) => {
    setCreateNew(false);
    // Auto-select the newly created collection
    setSelectedItems(collectionId);
  };

  if (createNew) {
    return (
      <AddCollectionName
        isOpen={createNew}
        onClose={onNewCollectionClose}
        onSuccess={onNewCollectionSuccess}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <>
          <Box sx={modalHeaderStyle}>
            <Text.Lead>Add to a lead list</Text.Lead>
          </Box>
          <Loading>
            <form onSubmit={handleConfirm}>
              <Box sx={modalBodyStyle}>
                <FlexRow sx={formHeaderStyle}>
                  <CKText>
                    {selectedItems ? '1 lead list' : '0 lead lists'} selected
                  </CKText>
                  <PlusButton
                    color="secondary"
                    onClick={() => setCreateNew(true)}
                    sx={{
                      padding: '13px 19px',
                      fontSize: '12px',
                      lineHeight: '14px',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Create new lead list
                  </PlusButton>
                </FlexRow>
                <FormControl>
                  <FlexColumn sx={formBodyStyle}>
                    {collectionList &&
                      [...collectionList].map(({ id, name }, index) => (
                        <FlexColumn key={id}>
                          {index === 0 && <Separator />}
                          <Checkbox
                            size="small"
                            sx={checkboxStyle}
                            key={id}
                            value={id}
                            isChecked={selectedItems ? selectedItems === id : false}
                            onToggle={() => setSelectedItems(id)}
                          >
                            <FlexRow sx={checkboxLabelStyle}>
                              <Text.BodyBold>{capitalize(name)}</Text.BodyBold>
                            </FlexRow>
                          </Checkbox>

                          {collectionList && collectionList?.length !== index + 1 && (
                            <Separator />
                          )}
                          {collectionList?.length === index + 1 && <Separator />}
                        </FlexColumn>
                      ))}
                  </FlexColumn>
                </FormControl>
              </Box>
              <ModalFooter sx={modalFooterStyle}>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isDisabled={!selectedItems}>
                  Add to selected
                </Button>
              </ModalFooter>
            </form>
          </Loading>
        </>
      </ModalContent>
    </Modal>
  );
};

export default AddProjectToCollectionModal;
